import { useMemo } from 'react';
import { SELECTED_CROP_SEASONS_STORAGE_KEY as STORAGE_KEY } from 'utilities/cropSeasonUrl';

/**
 * Reading `window.localStorage` itself throws a SecurityError when storage is
 * blocked (Safari private mode), so every access goes through here.
 */
const readRaw = (): string | null => {
  try {
    return globalThis.window?.localStorage?.getItem(STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
};

const parseSelectedCropSeasonIds = (raw: string | null): string[] => {
  if (raw === null) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id): id is string => typeof id === 'string');
  } catch {
    return [];
  }
};

const useSelectedCropSeasonIds = () => {
  const storageValue = readRaw();

  /**
   * @description
   * Returns the selected crop season ids from localStorage
   * @returns The selected crop season ids
   */
  const selectedCropSeasonIds = useMemo(
    () => parseSelectedCropSeasonIds(storageValue),
    [storageValue]
  );

  /**
   * @description
   * Checks if all the given crop season ids are selected
   * @param cropSeasonIds - The crop season ids to check
   * @returns True if all the given crop season ids are selected, false otherwise
   */
  const hasAllSelectedCropSeasonIds = (cropSeasonIds: string[]) => {
    return cropSeasonIds.every(id => selectedCropSeasonIds.includes(id));
  };

  /**
   * @description
   * Overwrites the selection. An empty list is ignored, since it would scope
   * the destination to nothing.
   * @param cropSeasonIds - The crop season ids to select
   */
  const setSelectedCropSeasonIds = (cropSeasonIds: string[]) => {
    if (!cropSeasonIds.length) return;
    try {
      globalThis.window?.localStorage?.setItem(
        STORAGE_KEY,
        JSON.stringify(cropSeasonIds)
      );
    } catch {
      // Blocked or full storage: degrade silently rather than fail the click.
    }
  };

  return {
    selectedCropSeasonIds,
    hasAllSelectedCropSeasonIds,
    setSelectedCropSeasonIds
  };
};

export default useSelectedCropSeasonIds;
